import React, { useState, useEffect } from 'react';
import { BeakerIcon } from '@heroicons/react/24/outline';
import { Medicine, CreateMedicineDto } from '../../types/medicines';
import { Disease } from '../../types/diseases';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface AddMedicineModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateMedicineDto) => Promise<void>;
  diseases: Disease[];
  medicine?: Medicine;
}

const AddMedicineModal: React.FC<AddMedicineModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  diseases,
  medicine,
}) => {
  const [formData, setFormData] = useState<CreateMedicineDto>({
    name: '',
    description: '',
    advantages: '',
    recommendedUse: '',
    activeIngredients: '',
    dosage: '',
    contraindications: '',
    sideEffects: '',
    manufacturer: '',
    prescriptionRequired: false,
    diseaseIds: [],
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (medicine) {
      setFormData({
        name: medicine.name,
        description: medicine.description || '',
        advantages: medicine.advantages || '',
        recommendedUse: medicine.recommendedUse || '',
        activeIngredients: medicine.activeIngredients || '',
        dosage: medicine.dosage || '',
        contraindications: medicine.contraindications || '',
        sideEffects: medicine.sideEffects || '',
        manufacturer: medicine.manufacturer || '',
        prescriptionRequired: medicine.prescriptionRequired || false,
        diseaseIds: medicine.diseases?.map(d => d.id) || [],
      });
    } else {
      setFormData({
        name: '',
        description: '',
        advantages: '',
        recommendedUse: '',
        activeIngredients: '',
        dosage: '',
        contraindications: '',
        sideEffects: '',
        manufacturer: '',
        prescriptionRequired: false,
        diseaseIds: [],
      });
    }
  }, [medicine, isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleDiseaseChange = (diseaseId: number) => {
    setFormData(prev => ({
      ...prev,
      diseaseIds: prev.diseaseIds?.includes(diseaseId)
        ? prev.diseaseIds.filter(id => id !== diseaseId)
        : [...(prev.diseaseIds || []), diseaseId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Please fill in the medicine name');
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Error creating medicine:', error);
      alert('Failed to create medicine. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader
        title={
          <span className="inline-flex items-center">
            <BeakerIcon className="h-6 w-6 text-blue-500 mr-2" />
            {medicine ? 'Edit Medicine' : 'Add Medicine'}
          </span>
        }
        onClose={onClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-4 max-h-96 overflow-y-auto">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Medicine Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Medicine Name *
              </label>              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Amoxicillin"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Manufacturer */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Manufacturer
              </label>
              <input
                type="text"
                name="manufacturer"
                value={formData.manufacturer}
                onChange={handleInputChange}
                placeholder="e.g., Pfizer"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Prescription Required */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="prescriptionRequired"
                checked={formData.prescriptionRequired}
                onChange={handleInputChange}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-slate-900">
                Prescription Required
              </label>
            </div>

            {/* Description */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                placeholder="Brief description of the medicine..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Active Ingredients */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Active Ingredients
              </label>
              <textarea
                name="activeIngredients"
                value={formData.activeIngredients}
                onChange={handleInputChange}
                rows={2}
                placeholder="List of active ingredients..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Dosage */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Dosage Information
              </label>
              <textarea
                name="dosage"
                value={formData.dosage}
                onChange={handleInputChange}
                rows={2}
                placeholder="Recommended dosage and administration..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Advantages */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Advantages
              </label>
              <textarea
                name="advantages"
                value={formData.advantages}
                onChange={handleInputChange}
                rows={3}
                placeholder="Benefits and advantages of this medicine..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Recommended Use */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Recommended Use
              </label>
              <textarea
                name="recommendedUse"
                value={formData.recommendedUse}
                onChange={handleInputChange}
                rows={3}
                placeholder="How and when to use this medicine..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Contraindications */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Contraindications
              </label>
              <textarea
                name="contraindications"
                value={formData.contraindications}
                onChange={handleInputChange}
                rows={3}
                placeholder="When this medicine should not be used..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Side Effects */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Side Effects
              </label>
              <textarea
                name="sideEffects"
                value={formData.sideEffects}
                onChange={handleInputChange}
                rows={3}
                placeholder="Known side effects and adverse reactions..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Treats Diseases */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Treats Diseases
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {diseases.map((disease) => (
                  <div key={disease.id} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`disease-${disease.id}`}
                      checked={formData.diseaseIds?.includes(disease.id) || false}
                      onChange={() => handleDiseaseChange(disease.id)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`disease-${disease.id}`} className="ml-2 text-sm text-slate-700">
                      {disease.name}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {submitting ? 'Saving...' : (medicine ? 'Update Medicine' : 'Add Medicine')}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default AddMedicineModal; 